import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Pricing() {
  const { t } = useTranslation();

  const plans = [
    {
      name: 'Classic',
      price: '29.99',
      description: 'Perfect for individuals getting started',
      features: [
        'Premium matte smart card',
        'Digital profile page',
        'vCard download',
        'Free worldwide shipping',
        'Lifetime access'
      ],
      popular: false
    },
    {
      name: 'Premium',
      price: '49.99',
      description: 'For professionals who want to stand out',
      features: [
        'Luxury metal smart card',
        'Custom engraving',
        'Priority support',
        'Advanced analytics',
        'Free worldwide shipping',
        'Lifetime access'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '99.99',
      description: 'For teams and organizations',
      features: [
        'Custom branded cards',
        'Team management',
        'Bulk ordering',
        'Dedicated support',
        'API access',
        'Free worldwide shipping',
        'Lifetime access'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-16">
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, idx) => (
            <motion.div
              key={idx}
              {...fadeUp}
              transition={{ delay: idx * 0.1 }}
              className={`rounded-3xl p-8 ${
                plan.popular 
                  ? 'bg-slate-900 text-white ring-4 ring-[#3B82F6]/20 scale-105' 
                  : 'bg-white border border-slate-200'
              }`}
              data-testid={`pricing-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <span className="inline-block px-3 py-1 bg-[#0A2540]/50 text-white text-xs font-medium rounded-full mb-4">
                  Most Popular
                </span>
              )}
              <h3 className={`font-heading text-2xl font-semibold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                {plan.name}
              </h3>
              <p className={`text-sm mb-4 ${plan.popular ? 'text-slate-300' : 'text-slate-600'}`}>
                {plan.description}
              </p>
              <div className="mb-6">
                <span className={`font-heading text-5xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                  €{plan.price}
                </span>
                <span className={plan.popular ? 'text-slate-300' : 'text-slate-500'}>
                  {' '}{t('pricing.oneTime')}
                </span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className={`flex items-center gap-3 text-sm ${plan.popular ? 'text-slate-300' : 'text-slate-600'}`}>
                    <Check className={`w-5 h-5 flex-shrink-0 ${plan.popular ? 'text-blue-400' : 'text-blue-500'}`} />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/products">
                <Button 
                  className={`w-full rounded-full py-6 ${
                    plan.popular 
                      ? 'bg-white text-slate-900 hover:bg-slate-100' 
                      : 'btn-primary'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {t('products.buyNow')}
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* FAQ */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl p-8 shadow-soft">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Are there any recurring fees?</h3>
              <p className="text-sm text-slate-600">No, IKATone is a one-time purchase. Your digital profile is free forever.</p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Can I upgrade later?</h3>
              <p className="text-sm text-slate-600">Yes, you can purchase additional cards or upgrade anytime.</p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900 mb-2">What payment methods do you accept?</h3>
              <p className="text-sm text-slate-600">We accept all major credit cards via Stripe secure checkout.</p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Do you offer refunds?</h3>
              <p className="text-sm text-slate-600">Yes, we offer a 30-day money-back guarantee if you're not satisfied.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
